import { useState, useEffect } from "react";
import { FileSignature, CreditCard, ExternalLink, CheckCircle2, FileText } from "lucide-react";
import api from "@/lib/api";

const B = process.env.REACT_APP_BACKEND_URL;
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const btn = "inline-flex items-center gap-1 text-sm font-bold px-3 py-1.5 rounded-lg";

const usePortalBilling = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/api/customer/portal-billing").then(r => setD(r.data)).catch(() => setD({ quotes: [], invoices: [] })); }, []);
  return d;
};

const Row = ({ q, children, testId }) => (
  <div className="flex flex-wrap items-center gap-3 py-3 border-b border-slate-100 last:border-0" data-testid={testId}>
    <div className="flex-1 min-w-[200px]">
      <p className="font-semibold text-slate-900">{q.title}{q.mode === "draft" && <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Draft</span>}</p>
      <p className="text-xs text-slate-500">{q.invoice_number} · {q.created_at.slice(0, 10)} · total {money(q.total)}{q.paid > 0 ? ` · paid ${money(q.paid)}` : ""}</p>
      {q.message && <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{q.message}</p>}
    </div>
    {children}
  </div>
);

export const PortalQuotes = () => {
  const d = usePortalBilling();
  if (!d) return null;
  if (!d.quotes.length) return <p className="text-sm text-slate-400 text-center py-8 bg-white border border-slate-200 rounded-xl mb-4" data-testid="portal-quotes-empty">No quotes yet — they'll appear here when your team sends one.</p>;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm mb-4" data-testid="portal-quotes">
      <h3 className="font-bold text-slate-900 mb-1">Quotes & proposals from your team</h3>
      <p className="text-xs text-slate-500 mb-2">Review, accept and sign. Once signed, it moves to Billing as an invoice.</p>
      {d.quotes.map(q => (
        <Row key={q.id} q={q} testId={`portal-quote-${q.id}`}>
          {q.has_contract && <a href={`${B}/api/documents/${q.id}/file`} target="_blank" rel="noreferrer" className="text-sm font-semibold text-blue-600 hover:underline flex items-center gap-1"><FileText className="w-3.5 h-3.5" />Growth plan</a>}
          {q.share_token ? <a href={`/p/${q.share_token}#${q.has_contract ? "packet" : "respond"}`} className={`${btn} bg-green-600 hover:bg-green-500 text-white`} data-testid={`portal-quote-sign-${q.id}`}><FileSignature className="w-4 h-4" />Review & sign</a>
            : <a href={`${B}/api/documents/${q.id}/file`} target="_blank" rel="noreferrer" className="text-sm font-semibold text-blue-600 hover:underline">View</a>}
        </Row>
      ))}
    </div>
  );
};

export const PortalInvoices = () => {
  const d = usePortalBilling();
  if (!d?.invoices?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm mb-4" data-testid="portal-invoices">
      <h3 className="font-bold text-slate-900 mb-2">Invoices</h3>
      {d.invoices.map(q => (
        <Row key={q.id} q={q} testId={`portal-invoice-${q.id}`}>
          {q.balance >= 0.5 ? <span className="text-sm font-bold text-blue-700">{money(q.balance)} due</span> : <span className="text-sm text-green-700 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Paid</span>}
          <a href={`/invoice/${q.share_token}`} className={`${btn} ${q.balance >= 0.5 ? "bg-green-600 hover:bg-green-500 text-white" : "border border-slate-300 text-slate-700"}`} data-testid={`portal-invoice-open-${q.id}`}>
            {q.balance >= 0.5 ? <><CreditCard className="w-4 h-4" />View & pay</> : <><ExternalLink className="w-4 h-4" />View</>}
          </a>
        </Row>
      ))}
    </div>
  );
};

export const PaymentSummary = () => {
  const d = usePortalBilling();
  if (!d) return null;
  const all = [...d.invoices];
  const paid = all.reduce((s, x) => s + x.paid, 0);
  const due = all.reduce((s, x) => s + x.balance, 0);
  return (
    <div className="grid grid-cols-3 gap-3 mb-4" data-testid="payment-summary">
      {[["Invoices", all.length], ["Paid to date", money(paid)], ["Balance due", money(due)]].map(([l, v]) => (
        <div key={l} className="bg-slate-50 border border-slate-200 rounded-lg p-3"><p className="text-[11px] uppercase tracking-wide text-slate-500">{l}</p><p className="text-lg font-bold text-slate-900">{v}</p></div>
      ))}
    </div>
  );
};
